import type { IdMap } from "@opus-dt/shared-types";
import type { DraftLine } from "./useLineDraftState";

export function applyIdMapping(draft: DraftLine, idMap: IdMap): DraftLine {
  const map = (
    kind: keyof Omit<IdMap, "lineId">,
    tmpId: string | undefined | null
  ): string | null => {
    if (!tmpId) return null;
    return idMap[kind][tmpId] ?? null;
  };

  const newMachines: DraftLine["machines"] = {};
  for (const [tmpMachineId, m] of Object.entries(draft.machines)) {
    const realMachineId = map("machines", tmpMachineId);
    if (!realMachineId) {
      throw new Error(`idMap incompleto: máquina ${tmpMachineId} sem real_id`);
    }
    newMachines[realMachineId] = {
      ...m,
      tmpId: realMachineId,
      parameters: m.parameters.map((p) => {
        const realId = map("parameters", p.tmpId);
        if (!realId) throw new Error(`idMap incompleto: parameter ${p.tmpId}`);
        return { ...p, tmpId: realId as never };
      }),
      sensorLinks: m.sensorLinks.map((sl) => {
        const realId = map("sensorLinks", sl.tmpId);
        if (!realId) throw new Error(`idMap incompleto: sensorLink ${sl.tmpId}`);
        const realParamId = sl.parameterTmpId ? map("parameters", sl.parameterTmpId) : null;
        return {
          ...sl,
          tmpId: realId as never,
          parameterTmpId: realParamId ? realParamId : sl.parameterTmpId,
        };
      }),
      compatibilities: m.compatibilities.map((c) => {
        const realId = map("compatibilities", c.tmpId);
        if (!realId) throw new Error(`idMap incompleto: compatibility ${c.tmpId}`);
        return { ...c, tmpId: realId as never };
      }),
    };
  }

  const newNodes = draft.canvas.nodes.map((n) => {
    const realNodeId = map("nodes", n.tmpId);
    if (!realNodeId) throw new Error(`idMap incompleto: node ${n.tmpId}`);
    const realMachineId = n.machineTmpId ? map("machines", n.machineTmpId) : null;
    return {
      ...n,
      tmpId: realNodeId as never,
      machineTmpId: realMachineId ? realMachineId : n.machineTmpId,
    };
  });

  const newEdges = draft.canvas.edges.map((e) => {
    const realEdgeId = map("edges", e.tmpId);
    const realSrc = map("nodes", e.sourceNodeTmpId);
    const realTgt = map("nodes", e.targetNodeTmpId);
    if (!realEdgeId || !realSrc || !realTgt) {
      throw new Error(`idMap incompleto: edge ${e.tmpId}`);
    }
    return {
      ...e,
      tmpId: realEdgeId as never,
      sourceNodeTmpId: realSrc as never,
      targetNodeTmpId: realTgt as never,
    };
  });

  return {
    header: draft.header,
    canvas: { nodes: newNodes, edges: newEdges },
    machines: newMachines,
    dirty: false,
  };
}
